import { Theme } from "@mui/material";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { generateTheme } from "../utils/Theme/theme";
import {
  headerTypes,
  healthScoreGroups,
  prodClient,
  routes,
  screenVarintswithDiferentColor,
} from "../utils/constants";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";

interface IAppContext {
  currentScreen: string;
  setCurrentScreen: (value: string) => void;
  mode: string;
  setMode: (value: string) => void;
  theme: Theme;
  setTheme: (value: Theme) => void;
  currentClinic: string;
  setCurrentClinic: (value: string) => void;
  googleApiKey: string;
  setgoogleApiKey: (value: string) => void;
  markTestAsCompleted: (testName: string) => void;
  tests: {
    name: string;
    isVisited: boolean;
    hasCompleted: boolean;
    translatedName: string;
    minimumRequirement: number;
    textToDispaly: string;
    maxLimit: number;
  }[];
  setTests: React.Dispatch<
    React.SetStateAction<
      {
        name: string;
        isVisited: boolean;
        hasCompleted: boolean;
        translatedName: string;
        minimumRequirement: number;
        textToDispaly: string;
        maxLimit: number;
      }[]
    >
  >;
  headerText: string;
  setHeaderText: (value: string) => void;
  showInappHeader: string;
  setShowInappHeader: (value: string) => void;
  nextScreen: string;
  setNextScreen: (value: string) => void;
  apploading: boolean;
  setAppLoading: (value: boolean) => void;
  appStartloading: boolean;
  setAppStartloading: (value: boolean) => void;
  showProfileIcon: boolean;
  setShowProfileIcon: (value: boolean) => void;
  appBarText: string;
  setAppBarText: (value: string) => void;
  currentActiveBottomNav: string;
  setCurrentActiveBottomNav: (value: string) => void;
  showLayoutFooter: boolean;
  setShowLayoutFooter: (value: boolean) => void;
  language: string;
  setLanguage: (value: string) => void;
  changeLanguage: (lang: string) => void;
  // Snackbar functions
  onShowSnackbarWithAction: (
    message: string,
    severity: "success" | "error" | "warning" | "info",
    actionRoute?: string,
    duration?: number
  ) => void;
  onHideSnackbar: () => void;
  setShowSnackbar: (value: boolean) => void;
  showSnackbar: boolean;
  snackbarMessage: string | null;
  setSnackbarMessage: (value: string | null) => void;
  snackbarSeverity: "success" | "error" | "warning" | "info";
  setSnackbarSeverity: (
    value: "success" | "error" | "warning" | "info"
  ) => void;
  snackbarActionRoute: string | null;
  setSnackbarActionRoute: (value: string | null) => void;
  loadingAppState: boolean;
  setLoadingAppState: (value: boolean) => void;
  error: any;
  setError: (value: any) => void;
  showBurger: boolean;
  setShowBurger: (value: boolean) => void;
  showSideDrawer: boolean;
  setShowSideDrawer: (value: boolean) => void;
  showConfirmationScreen: boolean;
  setShowConfirmationScreen: (value: boolean) => void;
  showEiraHeaderLogo: boolean;
  setShowEiraHeaderLogo: (value: boolean) => void;
  showBackIcon: boolean;
  setShowBackIcon: (value: boolean) => void;
  showMoreVertical: boolean;
  setShowMoreVertical: (value: boolean) => void;
  showBottomHomeIcon: boolean;
  setShowBottomHomeIcon: (value: boolean) => void;
  snackbarDuration: number | null;
  setSnackbarDuration: (value: number | null) => void;
  showCart: boolean;
  setShowCart: (value: boolean) => void;
  isSkiptoHomeonSymptomForm: boolean;
  setIsSkiptoHomeonSymptomForm: (value: boolean) => void;
  homeRouteLocation: string;
  setHomeRouteLocation: (value: string) => void;
  markTestAsVisited: (testName: string) => void;
  changeColoronScreens: string;
  setChangeColoronScreens: (vlaue: string) => void;
  profileActiveTab: number;
  setProfileActiveTab: React.Dispatch<React.SetStateAction<number>>;

  // Auth related
  canDownloadPdf: boolean;
  setCanDownlaodPdf: (value: boolean) => void;
  showBottomBorder: boolean;
  setShowBottomBorder: (value: boolean) => void;
  isRouteLoadedWhenAppoads: boolean;
  setIsRouteLoadedWhenAppoads: (value: boolean) => void;
  routeLocation: string;
  setRouteLocation: (value: string) => void;
  reloadRoutes: boolean;
  setReloadRoutes: (value: boolean) => void;
  triggerRouteReload: () => void;
  isInitialRouteSetupComplete: boolean;
  markInitialRouteSetupComplete: () => void;
  clearNavigationHistory: () => void;
  activePrescriptionId: null | number | string;
  setActivePrescriptionId: (value: null | number | string) => void;
  currentTest: string;
  setCurrentTest: (value: string) => void;
}

export const appContext = createContext<IAppContext>({} as IAppContext);

const useAppStateProvider = () => {
  const { t } = useTranslation();

  const [changeColoronScreens, setChangeColoronScreens] = useState<string>(
    screenVarintswithDiferentColor[0]
  );

  // load routes when loaded
  const [isRouteLoadedWhenAppoads, setIsRouteLoadedWhenAppoads] =
    useState<boolean>(true);
  const [routeLocation, setRouteLocation] = useState<string>(routes.AboutEira);

  // ✅ Snackbar State
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");
  const [snackbarActionRoute, setSnackbarActionRoute] = useState<string | null>(
    null
  );
  const [snackbarDuration, setSnackbarDuration] = useState<number | null>(null);

  // current clinic
  const [currentClinic, setCurrentClinic] = useState<string>(prodClient);

  // theme
  const [mode, setMode] = useState<string>("light");
  const [theme, setTheme] = useState<Theme>(generateTheme("light", prodClient));

  const [loadingAppState, setLoadingAppState] = useState<boolean>(true);
  const [currentScreen, setCurrentScreen] = useState<string>("");

  const [googleApiKey, setgoogleApiKey] = useState("");
  const [headerText, setHeaderText] = useState<string>("");
  const [showInappHeader, setShowInappHeader] = useState<string>(
    headerTypes.base
  );
  const [showProfileIcon, setShowProfileIcon] = useState<boolean>(false);
  const [nextScreen, setNextScreen] = useState<string>("");
  const [apploading, setAppLoading] = useState<boolean>(false); // Changed from true to false since joyride manages loading
  const [appStartloading, setAppStartloading] = useState<boolean>(false);
  const [reloadRoutes, setReloadRoutes] = useState<boolean>(false);
  const [isInitialRouteSetupComplete, setIsInitialRouteSetupComplete] =
    useState<boolean>(false);
  const [appBarText, setAppBarText] = useState<string>("");
  const [currentActiveBottomNav, setCurrentActiveBottomNav] =
    useState<string>("");
  const [showLayoutFooter, setShowLayoutFooter] = useState<boolean>(false);

  // error state
  const [error, setError] = useState<any>(null);

  // showBurgerForSideDrawer
  const [showBurger, setShowBurger] = useState<boolean>(false);

  // side-drawer
  const [showSideDrawer, setShowSideDrawer] = useState<boolean>(false);

  // basic deatils confirmation screen
  const [showConfirmationScreen, setShowConfirmationScreen] =
    useState<boolean>(false);

  const [showMoreVertical, setShowMoreVertical] = useState<boolean>(false);

  // show eira header logo
  const [showEiraHeaderLogo, setShowEiraHeaderLogo] = useState<boolean>(true);
  const [showBackIcon, setShowBackIcon] = useState<boolean>(false);

  // shwo cart
  const [showCart, setShowCart] = useState<boolean>(false);

  // footer
  const [isSkiptoHomeonSymptomForm, setIsSkiptoHomeonSymptomForm] =
    useState(true);
  const [homeRouteLocation, setHomeRouteLocation] = useState("");

  // profile active tab
  const [profileActiveTab, setProfileActiveTab] = useState<number>(0);

  // pdf download signal
  const [canDownloadPdf, setCanDownlaodPdf] = useState(false);
  const [activePrescriptionId, setActivePrescriptionId] = useState<
    null | number | string
  >(null);

  // bottom border in the header
  const [showBottomBorder, setShowBottomBorder] = useState<boolean>(true);

  // for current test user is attempting
  const [currentTest, setCurrentTest] = useState<string>("");
  const [showBottomHomeIcon, setShowBottomHomeIcon] = useState(true);

  // ✅ Shows a Snackbar with a message and severity
  const onShowSnackbarWithAction = (
    message: string,
    severity: "success" | "error" | "warning" | "info",
    actionRoute?: string,
    duration?: number
  ) => {
    setShowSnackbar(true);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarActionRoute(actionRoute || null);
    setSnackbarDuration(duration || 2000);
  };

  // ✅ Reset the snackbar message
  const onHideSnackbar = () => {
    setShowSnackbar(false);
    setSnackbarMessage(null);
    setSnackbarActionRoute(null);
    setSnackbarDuration(2000);
  };

  // Load language from localStorage if available, default to "en"
  const [language, setLanguage] = useState<string>("en");

  //  Add method to trigger route reload only when needed
  const triggerRouteReload = useCallback(() => {
    setReloadRoutes(true);
  }, []);

  //  Add method to mark initial setup as complete
  const markInitialRouteSetupComplete = useCallback(() => {
    setIsInitialRouteSetupComplete(true);
  }, []);

  // ✅ Load Language on App Load
  useEffect(() => {
    const storedLang = localStorage.getItem("language") || "en";
    setLanguage(storedLang);
    i18n.changeLanguage(storedLang);
    setLoadingAppState(false);
  }, []);

  // ✅ Function to Change Language
  const changeLanguage = (lang: string) => {
    if (!["en", "hi", "ml", "kn", "ru"].includes(lang)) return;
    if (lang !== language) {
      setLanguage(lang);
      localStorage.setItem("language", lang);
      i18n.changeLanguage(lang);
    }
  };

  // Function to clear navigation history on fresh app loads
  const clearNavigationHistory = useCallback(() => {
    try {
      sessionStorage.removeItem("hasNavigatedBefore");
    } catch (error) {
      console.warn("Could not clear navigation history:", error);
    }
  }, []);

  useEffect(() => {
    if (
      process.env.REACT_APP_GOOGLE_API_KEY &&
      process.env.REACT_APP_GOOGLE_API_KEY.length > 0
    )
      setgoogleApiKey(process.env.REACT_APP_GOOGLE_API_KEY);
  }, []);

  // Test list - simplified since joyride context manages the complex state
  const [tests, setTests] = useState([
    {
      name: "Health History",
      isVisited: false,
      hasCompleted: false,
      translatedName: t("HealthHistory"),
      minimumRequirement: 0,
      maxLimit: 29,
      textToDispaly: "0/25",
    },
    {
      name: "Prakriti Profiling",
      isVisited: false,
      hasCompleted: false,
      translatedName: t("PrakritiProfile"),
      minimumRequirement: 0,
      maxLimit: 30,
      textToDispaly: "0/25",
    },
    {
      name: "Blood Reports",
      isVisited: false,
      hasCompleted: false,
      translatedName: t("BloodReports"),
      minimumRequirement: 0,
      maxLimit: 1,
      textToDispaly: "Status: Pending",
    },
    {
      name: "Tongue Analysis",
      isVisited: false,
      hasCompleted: false,
      translatedName: t("TongueAnalysis"),
      minimumRequirement: 0,
      maxLimit: 1,
      textToDispaly: "Status: Pending",
    },
    {
      name: "Dosha Imbalance Assessment",
      isVisited: false,
      hasCompleted: false,
      translatedName: t("DoshaImbalanceAssess"),
      minimumRequirement: 0,
      maxLimit: 0,
      textToDispaly: "Status: Pending",
    },
    {
      name: "Health Score",
      isVisited: false,
      hasCompleted: false,
      translatedName: t("HealthScores"),
      minimumRequirement: 0,
      maxLimit: healthScoreGroups[0].length,
      textToDispaly: "Status: Pending",
    },
  ]);

  // Helper function to get translation key
  const getTranslationKey = (name: string) => {
    switch (name) {
      case "Health History":
        return t("HealthHistory");
      case "Blood Reports":
        return t("BloodReportsButtonText");
      case "Tongue Analysis":
        return t("TongueAnalysis");
      case "Prakriti Profiling":
        return t("PrakritiProfile");
      case "Dosha Imbalance Assessment":
        return t("DoshaImbalanceAssess");
      case "Health Score":
        return t("HealthScores");
      default:
        return "";
    }
  };

  useEffect(() => {
    setTests((prevTests) =>
      prevTests.map((test) => ({
        ...test,
        translatedName: t(getTranslationKey(test.name)),
      }))
    );
  }, [language]);

  const markTestAsVisited = (testName: string) => {
    setTests((prev) => {
      const updated = prev.map((test) =>
        test.name === testName ? { ...test, isVisited: true } : test
      );
      return updated;
    });
  };

  const markTestAsCompleted = (testName: string) => {
    setTests((prev) =>
      prev.map((test) =>
        test.name === testName
          ? { ...test, hasCompleted: true, isVisited: true }
          : test
      )
    );
  };

  return {
    currentScreen,
    setCurrentScreen,
    theme,
    setTheme,
    mode,
    setMode,
    currentClinic,
    setCurrentClinic,
    googleApiKey,
    setgoogleApiKey,
    markTestAsCompleted,
    tests,
    setTests,
    headerText,
    setHeaderText,
    showInappHeader,
    setShowInappHeader,
    nextScreen,
    setNextScreen,
    apploading,
    setAppLoading,
    showProfileIcon,
    setShowProfileIcon,
    appBarText,
    setAppBarText,
    currentActiveBottomNav,
    setCurrentActiveBottomNav,
    showLayoutFooter,
    setShowLayoutFooter,
    language,
    setLanguage,
    changeLanguage,
    onShowSnackbarWithAction,
    onHideSnackbar,
    setShowSnackbar,
    showSnackbar,
    snackbarMessage,
    setSnackbarMessage,
    snackbarSeverity,
    setSnackbarSeverity,
    snackbarActionRoute,
    setSnackbarActionRoute,
    loadingAppState,
    setLoadingAppState,
    error,
    setError,
    showBurger,
    setShowBurger,
    showSideDrawer,
    setShowSideDrawer,
    showConfirmationScreen,
    setShowConfirmationScreen,
    showEiraHeaderLogo,
    setShowEiraHeaderLogo,
    showBackIcon,
    setShowBackIcon,
    showMoreVertical,
    setShowMoreVertical,
    showBottomHomeIcon,
    setShowBottomHomeIcon,
    snackbarDuration,
    setSnackbarDuration,
    showCart,
    setShowCart,
    homeRouteLocation,
    setHomeRouteLocation,
    isSkiptoHomeonSymptomForm,
    setIsSkiptoHomeonSymptomForm,
    markTestAsVisited,
    changeColoronScreens,
    setChangeColoronScreens,
    profileActiveTab,
    setProfileActiveTab,
    canDownloadPdf,
    setCanDownlaodPdf,
    showBottomBorder,
    setShowBottomBorder,
    routeLocation,
    setRouteLocation,
    isRouteLoadedWhenAppoads,
    setIsRouteLoadedWhenAppoads,
    appStartloading,
    setAppStartloading,
    reloadRoutes,
    setReloadRoutes,
    triggerRouteReload,
    isInitialRouteSetupComplete,
    markInitialRouteSetupComplete,
    clearNavigationHistory,
    activePrescriptionId,
    setActivePrescriptionId,
    currentTest,
    setCurrentTest,
  };
};

interface Props {
  children: React.ReactNode;
}

export const AppStateProvider = ({ children }: Props) => {
  const app = useAppStateProvider();
  const memoizedValue = useMemo(() => app, [app.language]);

  return <appContext.Provider value={app}>{children}</appContext.Provider>;
};

export const useAppState = () => {
  return useContext(appContext);
};
